import { useMouseMove } from "@asup/context-menu";
import React, { useCallback, useEffect, useLayoutEffect, useRef } from "react";
import { classMerge } from "../utility/classMerge";
import { HeaderSpanMap } from "./computeHeaderSpanState";
import { MIN_SCAFFOLD_COL_WIDTH_PX } from "./interface";
import { useScaffold } from "./Scaffold";
import styles from "./Scaffold.module.css";

export interface ColResizeHandleProps {
  /** Column id being potentially resized. */
  colId: string;
}

/**
 * Determines whether a rendered scaffold cell covers the target logical column.
 *
 * @param headerSpanMap - Pre-computed header span information from scaffold context.
 * @param orderedColIds - Ordered column ids by visual position.
 * @param rowId - Row id of the rendered cell.
 * @param cellColId - Logical column id of the rendered cell.
 * @param targetColId - Column id currently being resized.
 * @returns True when the rendered cell width should track the resized column.
 */
export const doesRenderedCellCoverColumn = (
  headerSpanMap: HeaderSpanMap | undefined,
  orderedColIds: string[],
  rowId: string | undefined,
  cellColId: string,
  targetColId: string,
): boolean => {
  if (cellColId === targetColId) {
    return true;
  }

  if (headerSpanMap === undefined || rowId === undefined) {
    return false;
  }

  const rowSpanInfo = headerSpanMap[rowId];
  const cellInfo = rowSpanInfo?.[cellColId];
  if (cellInfo?.mode !== "spanning" || cellInfo.effectiveWidth === undefined) {
    return false;
  }

  const startIndex = orderedColIds.indexOf(cellColId);
  const targetIndex = orderedColIds.indexOf(targetColId);
  if (startIndex < 0 || targetIndex < 0 || targetIndex <= startIndex) {
    return false;
  }

  for (let i = startIndex + 1; i <= targetIndex; i++) {
    const coveredInfo = rowSpanInfo?.[orderedColIds[i]];
    if (coveredInfo?.mode !== "covered-same-row") {
      return false;
    }
  }

  return true;
};

/**
 * Resize handle rendered at the right edge of a scaffold column cell (or at the left edge for
 * right-justified scaffolds).
 * Dragging the handle updates all cells sharing the same column id directly via DOM styles,
 * committing a single column-width update on mouse release.
 * Calculates handle position and canResize state from scaffold context.
 * The delta multiplier depends on the scaffold's justification:
 * - "left" (default): 1:1 right-edge drag
 * - "center": 2× delta (table expands symmetrically, column edge moves at half mouse rate)
 * - "right": −1× delta on left edge (dragging left widens the column)
 *
 * @param props - Component props.
 * @param props.colId - Column id being potentially resized.
 * @returns A resize handle div, rendered as disabled when canResize is false.
 */
export const ScaffoldColResizeHandle = ({ colId }: ColResizeHandleProps) => {
  const scaffoldContext = useScaffold();
  const leavingCol = scaffoldContext.state.leavingCols.find((c) => c.id === colId);
  const enteringCol = scaffoldContext.state.enteringColIds.has(colId);
  const col = scaffoldContext.settings.columns.find((c) => c.id === colId) ?? leavingCol;
  const justification = scaffoldContext.settings.justification ?? "left";

  // Calculate left offset by accumulating column widths (including this column)
  const orderedCols = [
    ...scaffoldContext.settings.columns,
    ...scaffoldContext.state.leavingCols,
  ].sort((a, b) => a.colNumber - b.colNumber);
  const orderedColIds = orderedCols.map((orderedCol) => orderedCol.id);
  let leftOffset = 0;
  for (const c of orderedCols) {
    leftOffset += scaffoldContext.state.previewColWidths[c.id] ?? c.width;
    if (c.id === colId) {
      break;
    }
  }

  // For right justification the handle sits at the left edge of the column so that
  // dragging leftward widens the column as the table grows toward the left.
  const thisColWidth = scaffoldContext.state.previewColWidths[colId] ?? col?.width ?? 0;
  const handleLeftOffset = justification === "right" ? leftOffset - thisColWidth : leftOffset;

  // Derive canResize from context state
  const isDragging = scaffoldContext.state.isDragging === true;
  const canResize =
    scaffoldContext.settings.disabled !== true &&
    scaffoldContext.settings.onChangeCol !== undefined &&
    !isDragging &&
    !leavingCol &&
    !enteringCol &&
    col !== undefined;

  const resizeSessionRef = useRef<{
    startX: number;
    startWidth: number;
    latestWidth: number;
    targetCells: {
      cell: HTMLDivElement;
      startWidth: number;
    }[];
  } | null>(null);
  const colRef = useRef(col);
  const leavingColRef = useRef(leavingCol);
  const onChangeColRef = useRef(scaffoldContext.settings.onChangeCol);
  const scaffoldRootRef = useRef<HTMLElement | null>(null);

  /**
   * Adds the cellResizing CSS class to the scaffold root so all cells show the col-resize
   * cursor throughout the drag, regardless of where the pointer is within the scaffold.
   */
  const applyResizingClass = useCallback(() => {
    /* istanbul ignore else -- scaffoldRootRef is always set before applyResizingClass is called */
    if (scaffoldRootRef.current !== null) {
      scaffoldRootRef.current.classList.add(styles.cellResizing);
    }
  }, []);

  /**
   * Removes the cellResizing CSS class from the scaffold root after a resize session ends.
   */
  const clearResizingClass = useCallback(() => {
    /* istanbul ignore else -- scaffoldRootRef is always set before clearResizingClass is called */
    if (scaffoldRootRef.current !== null) {
      scaffoldRootRef.current.classList.remove(styles.cellResizing);
    }
  }, []);

  /**
   * Keeps mutable refs synchronised for document-level column resize handlers.
   */
  useLayoutEffect(() => {
    colRef.current = col;
    leavingColRef.current = leavingCol;
    onChangeColRef.current = scaffoldContext.settings.onChangeCol;
  }, [col, leavingCol, scaffoldContext.settings.onChangeCol]);

  /**
   * Applies column width updates during drag by manipulating DOM style directly (no React state).
   * Uses delta-based calculation from initial width for instant visual feedback and jsdom compatibility.
   * The delta is multiplied by 2 for centre-justified tables (the table expands symmetrically so
   * the column edge moves at half the mouse rate) and inverted for right-justified tables (the handle
   * sits at the left edge and dragging left widens the column).
   *
   * @param e - Native mouse move event emitted on the window.
   */
  const handleResizeMouseMove = (e: React.MouseEvent<Element, MouseEvent>) => {
    const session = resizeSessionRef.current;
    /* istanbul ignore next -- listeners only attached during active resize */
    if (session === null) {
      return;
    }

    const deltaMultiplier = justification === "center" ? 2 : justification === "right" ? -1 : 1;
    const nextWidth = Math.max(
      MIN_SCAFFOLD_COL_WIDTH_PX,
      Math.round(session.startWidth + deltaMultiplier * (e.clientX - session.startX)),
    );

    if (nextWidth === session.latestWidth) {
      return;
    }

    const widthDelta = nextWidth - session.startWidth;
    session.latestWidth = nextWidth;
    session.targetCells.forEach((targetCell) => {
      const targetWidth = Math.max(MIN_SCAFFOLD_COL_WIDTH_PX, targetCell.startWidth + widthDelta);
      targetCell.cell.style.width = `${targetWidth}px`;
    });
  };

  /**
   * Clears transient inline width styles from all cells participating in an active column resize.
   */
  const clearColumnResizeStyles = useCallback(() => {
    /* istanbul ignore next -- only called from handleResizeMouseUp when session is non-null */
    if (resizeSessionRef.current === null) {
      return;
    }

    resizeSessionRef.current.targetCells.forEach((targetCell) => {
      targetCell.cell.style.width = "";
    });
  }, []);

  /**
   * Reads an element's inline width in pixels, falling back to a provided default width.
   *
   * @param cell - Cell element whose width should be measured.
   * @param fallbackWidth - Width to use when no parseable inline width exists.
   * @returns The parsed or fallback width in pixels.
   */
  const getCellWidthOrFallback = useCallback(
    (cell: HTMLDivElement, fallbackWidth: number): number => {
      const parsedWidth = Math.round(parseFloat(cell.style.width));
      return parsedWidth || fallbackWidth;
    },
    [],
  );

  /**
   * Completes column resize by extracting final width from inline style and emitting single update.
   */
  const handleResizeMouseUp = useCallback(() => {
    const session = resizeSessionRef.current;
    /* istanbul ignore next -- listeners only attached during active resize */
    if (session === null) {
      clearResizingClass();
      scaffoldRootRef.current = null;
      return;
    }

    clearResizingClass();
    scaffoldRootRef.current = null;

    const width = session.latestWidth;
    resizeSessionRef.current = null;

    const currentCol = colRef.current;
    const currentLeavingCol = leavingColRef.current;
    if (currentCol !== undefined && !currentLeavingCol && width !== currentCol.width) {
      onChangeColRef.current?.(colId, {
        ...currentCol,
        width,
      });
    }
  }, [clearResizingClass, colId]);

  /**
   * Ensures document listeners are cleaned up on unmount.
   */
  useEffect(() => {
    return () => {
      clearResizingClass();
      scaffoldRootRef.current = null;
      clearColumnResizeStyles();
      resizeSessionRef.current = null;
    };
  }, [clearColumnResizeStyles, clearResizingClass]);

  /**
   * Starts a column width drag operation from this resize handle.
   *
   * @param event - React mouse down event from the resize handle.
   */
  const handleResizeMouseDown = (event: React.MouseEvent<HTMLElement | SVGElement>) => {
    if (canResize) {
      event.preventDefault();
      const scaffoldRoot = document.getElementById(scaffoldContext.settings.id);
      /* istanbul ignore else */
      if (scaffoldRoot !== null) {
        const targetCells = Array.from(
          scaffoldRoot.querySelectorAll("div[data-scaffold-col-id]"),
        ).filter((element): element is HTMLDivElement => {
          if (!(element instanceof HTMLDivElement)) {
            return false;
          }

          const cellRowId = element.dataset.scaffoldRowId;
          const cellColId = element.dataset.scaffoldColId;
          if (cellColId === undefined) {
            return false;
          }

          return doesRenderedCellCoverColumn(
            scaffoldContext.headerSpanMap,
            orderedColIds,
            cellRowId,
            cellColId,
            colId,
          );
        });

        /* istanbul ignore else */
        if (targetCells.length > 0) {
          const referenceCell = targetCells.find((targetCell) => {
            if (targetCell.dataset.scaffoldColId !== colId) {
              return false;
            }

            const targetRowId = targetCell.dataset.scaffoldRowId;
            const targetCellInfo =
              targetRowId !== undefined
                ? scaffoldContext.headerSpanMap?.[targetRowId]?.[colId]
                : undefined;
            return targetCellInfo?.mode !== "spanning";
          });
          const currentWidth =
            referenceCell !== undefined
              ? getCellWidthOrFallback(referenceCell, col.width)
              : col.width;

          resizeSessionRef.current = {
            startX: event.clientX,
            startWidth: currentWidth,
            latestWidth: currentWidth,
            targetCells: targetCells.map((targetCell) => ({
              cell: targetCell,
              startWidth: getCellWidthOrFallback(targetCell, currentWidth),
            })),
          };

          resizeSessionRef.current.targetCells.forEach((targetCell) => {
            targetCell.cell.style.width = `${targetCell.startWidth}px`;
          });

          scaffoldRootRef.current = scaffoldRoot;
          clearResizingClass();
          applyResizingClass();
        }
      }
    }
  };

  const { onMouseDown: onResizeHandleMouseDown } = useMouseMove({
    onMouseDown: handleResizeMouseDown,
    onMouseMove: handleResizeMouseMove,
    onMouseUp: handleResizeMouseUp,
    interactionEndEnabled: false,
    viewportResizeEnabled: false,
  });

  return (
    <div
      className={classMerge([
        styles.colResizeHandle,
        canResize ? undefined : styles.colResizeHandleDisabled,
      ])}
      style={{ left: handleLeftOffset }}
      onMouseDown={onResizeHandleMouseDown}
    />
  );
};

ScaffoldColResizeHandle.displayName = "ScaffoldColResizeHandle";
